import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAllMarkets } from './useMarkets';
import { getProductTotalsAbortable } from './useProducts';

const PAGE_SIZE = 12;
const DEBOUNCE_MS = 300;

export function useProductSelection({ show, api, organizationId, initialMarketId }) {
  const productApi = useMemo(() => api || {
    getProductTotalsAbortable,
    getMarkets: getAllMarkets,
  }, [api]);
  const [marketState, setMarketState] = useState({
    organizationId, options: [] as any[], selected: initialMarketId ? String(initialMarketId) : '',
  });
  const markets = marketState.organizationId === organizationId ? marketState.options : [];
  const marketId = marketState.organizationId === organizationId ? marketState.selected : '';
  const setMarketId = (selected: string) => setMarketState(previous => ({
    organizationId, options: previous.organizationId === organizationId ? previous.options : [], selected,
  }));
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [requestGeneration, setRequestGeneration] = useState(0);
  const [results, setResults] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const queryChangeRef = useRef(0);
  const marketRequestIdRef = useRef(0);
  const productRequestIdRef = useRef(0);
  const inFlightRef = useRef<{ cancel: () => void } | null>(null);

  const invalidateProducts = useCallback(() => {
    productRequestIdRef.current += 1;
    inFlightRef.current?.cancel();
    inFlightRef.current = null;
    setLoading(false);
    setResults([]);
    setTotal(0);
    setSelectedProduct(null);
  }, []);

  useEffect(() => {
    const requestId = ++marketRequestIdRef.current;
    setMarketState(previous => ({ organizationId, options: [],
      selected: previous.organizationId === organizationId ? previous.selected : '' }));
    if (!show || !organizationId) return;
    productApi.getMarkets?.(organizationId)
      .then(nextMarkets => {
        if (requestId === marketRequestIdRef.current) setMarketState(previous => ({ ...previous, options: nextMarkets || [] }));
      })
      .catch(() => {});
    return () => { marketRequestIdRef.current += 1; };
  }, [show, organizationId, productApi]);

  useEffect(() => {
    invalidateProducts();
    if (show) {
      queryChangeRef.current = 0;
      setMarketId(initialMarketId ? String(initialMarketId) : '');
      setQuery('');
      setDebouncedQuery('');
      setRequestGeneration(0);
      setPage(0);
    }
  }, [show, organizationId, initialMarketId, invalidateProducts]);

  useEffect(() => {
    if (queryChangeRef.current === 0) return;
    const queryChange = queryChangeRef.current;
    const timeout = window.setTimeout(() => {
      setDebouncedQuery(query);
      setPage(0);
      setRequestGeneration(queryChange);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    const requestId = ++productRequestIdRef.current;
    setResults([]);
    setTotal(0);
    setSelectedProduct(null);
    if (!show || !organizationId || !productApi.getProductTotalsAbortable
      || query !== debouncedQuery || queryChangeRef.current !== requestGeneration) {
      setLoading(false);
      return;
    }

    inFlightRef.current?.cancel();
    setLoading(true);
    const request = productApi.getProductTotalsAbortable({
      organizationId,
      marketId: marketId || null,
      name: debouncedQuery || null,
      page,
      pageSize: PAGE_SIZE,
      active: true,
    });
    inFlightRef.current = request;
    request.promise.then((response) => {
      if (requestId !== productRequestIdRef.current) return;
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.documents)
          ? response.documents
          : Array.isArray(response) ? response : [];
      setResults(data);
      setTotal(response?.total || response?.count || data.length);
    }).catch((error) => {
      if (requestId !== productRequestIdRef.current || error?.name === 'AbortError') return;
      console.error('[ProductSelectionModal] Failed to fetch products:', error);
    }).finally(() => {
      if (requestId === productRequestIdRef.current) {
        inFlightRef.current = null;
        setLoading(false);
      }
    });
    return () => {
      request.cancel();
      productRequestIdRef.current += 1;
    };
  }, [show, organizationId, query, debouncedQuery, marketId, page, productApi, requestGeneration]);

  const changeQuery = (value: string) => {
    queryChangeRef.current += 1;
    invalidateProducts();
    setQuery(value);
  };
  const changeMarket = (value: string) => {
    invalidateProducts();
    setMarketId(value);
    setPage(0);
  };
  const changePage = (nextPage: number) => {
    invalidateProducts();
    setPage(nextPage);
  };

  return {
    markets, marketId, query, results, total, page, loading, selectedProduct,
    setSelectedProduct, changeQuery, changeMarket, changePage, invalidateProducts,
  };
}
